import { useCallback, useEffect, useMemo, useState } from "react"

import createStore from "@Stores/base-factories"
import { hookIntegrationList, hookPostList, hookRewardList, hookTagList } from '@Stores/base-factories/store-list'
import hookSiteSelectedFactory from "@Stores/base-factories/store-site-selection"
import { hookGlobalGetTemplates } from "@Stores/global/factory";

const usePostFactory = ({ fetch, siteSelected: { id: siteId, key: siteKey }, collection, collections: { collectionsURL } }) => {
  const { collectionURL, item, save: savePost, reset, fetchById } = collection
  const [versions, setVersions] = useState(null)
  const [versionId, setVersionId] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    if (item?.id) {
      const { versions } = item
      setVersions(versions)

      if (versionId) {
        const version = versions?.find(({ id }) => id === versionId)
        setData({ ...item, ...version, id: item.id, versionId: version.id })
      } else {
        setData(item)
      }
    }
  }, [item, versionId])

  const onChangeVersion = useCallback(version => {
    setData(version ? { ...item, ...version, id: item.id, versionId: version.id } : item)
    setVersionId(version?.id || null)
  }, [item])

  const getReferences = useCallback(async postId => await fetch({
    url: `${collectionURL}/${postId}/references`,
    params: { [siteKey]: siteId },
  }), [fetch, siteId, siteKey, collectionURL])

  const uploadImage = useCallback(async file => {
    const data = new FormData()
    data.append('image', file)
    return await fetch({
      method: 'post',
      url: `${collectionsURL}/image`,
      data,
    })
  }, [fetch, collectionsURL])

  const createVersion = useCallback(async payload => {
    if (!data) return null
    const { title, content, template, custom, featured_image, active, content_type, site_id, id: cms_post_id, id } = data
    payload = { ...payload, title, content, template, custom, featured_image, active, content_type, site_id, cms_post_id }
    await fetch({
      method: 'post',
      url: `${collectionURL}/${id}/version`,
      data: payload,
    })
    reset()
    fetchById(id)
  }, [fetch, collectionURL, data, reset, fetchById])

  const saveVersion = useCallback(async payload => {
    const { id, versionId } = payload
    await fetch({
      method: 'put',
      url: `${collectionURL}/${id}/versions/${versionId}`,
      data: payload,
    })
    reset()
    fetchById(id)
    return { id }
  }, [fetch, collectionURL, reset, fetchById])

  const deleteVersion = useCallback(async cmsPostVersionId => {
    if (!data) return null
    await fetch({
      method: 'delete',
      url: `${collectionURL}/${data.id}/versions/${cmsPostVersionId}`,
    })
    reset()
    fetchById(data.id)
  }, [fetch, collectionURL, data, reset, fetchById])

  const save = useCallback(async payload => {
    if (payload.versionId) return saveVersion(payload)
    return savePost(payload)
  }, [saveVersion, savePost])

  const getSweepIntegrations = useCallback(async params => await fetch({
    url: `integrations`,
    params: { [siteKey]: siteId, ...params },
  }), [fetch, siteKey, siteId])

  return useMemo(() => ({
    collection: {
      ...collection, data,
      version: { versions, createVersion, saveVersion, deleteVersion, onChangeVersion },
      useSubmit: () => save,
    },

    getReferences, uploadImage, getSweepIntegrations,
  }), [collection, save, data, versions, createVersion, saveVersion, deleteVersion, onChangeVersion, getReferences, uploadImage, getSweepIntegrations])
}

const { Provider: PostProvider, useStore: usePostStore } = createStore()
  .extend(hookSiteSelectedFactory('site_id'))
  .extendHookCollectionFactory('cms/post')
  .extendHookCollectionsFactory('cms/posts')
  .extend(hookGlobalGetTemplates('cms/setting'))
  .extend(usePostFactory)
  .extend(hookRewardList('rewards'))
  .extend(hookTagList())
  .factory()

const { Provider: SCAProvider, useStore: useSCAStore } = createStore()
  .extend(hookSiteSelectedFactory('siteId'))
  .extendHookCollectionsFactory('cms/post/sca-winners')
  .extend(hookIntegrationList())
  .extend(hookPostList('posts'))
  .factory()

const { Provider: ContestsProvider, useStore: useContestsStore } = createStore()
  .extend(hookSiteSelectedFactory('siteId'))
  .extendHookCollectionsFactory('cms/post/contests')
  .extend(hookPostList())
  .factory()

const useContestsSubscriptionsBaseOverride = baseStore => useMemo(() => ({
  ...baseStore,
  fetch: params => {
    const { url, params: data } = params
    return baseStore.fetch({ method: 'post', url, data })
  },
}), [baseStore])

const useContestsSubscriptionsFactory = ({ fetch, siteSelected: { id: siteId, key: siteKey }, collections: { collectionsURL } }) => {
  const onExport = useCallback(async query => await fetch({
    method: 'POST',
    url: `${collectionsURL}/export`,
    params: { [siteKey]: siteId, ...query },
  }), [fetch, siteId, siteKey, collectionsURL])

  return useMemo(() => ({
    onExport,
  }), [onExport])
}

const { Provider: ContestsSubscriptionsProvider, useStore: useContestsSubscriptionsStore } = createStore()
  .extend(useContestsSubscriptionsBaseOverride)
  .extend(hookSiteSelectedFactory('siteId'))
  .extendHookCollectionsFactory('cms/post/contests/subscriptions')
  .extend(useContestsSubscriptionsFactory)
  .factory()

export {
  PostProvider,
  usePostStore,
  SCAProvider,
  useSCAStore,
  ContestsProvider,
  useContestsStore,
  ContestsSubscriptionsProvider,
  useContestsSubscriptionsStore,
}
